import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { ChevronDown, ArrowRight, Check, Mail, Settings, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Dashboard for Products & Services hub — KPI cards + onboarding checklist + tips. */
export default function DashboardProductsServices() {
  const [stats, setStats] = useState({ revenue: 0, orders: 0, aov: 0 });
  const [products, setProducts] = useState([]);
  const [recentEdited, setRecentEdited] = useState([]);
  const [hasPayment, setHasPayment] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/products`, { headers })
      .then(r => { setProducts(r.data || []); setRecentEdited((r.data || []).slice(0, 4)); })
      .catch(() => {});
    axios.get(`${API}/orders`, { headers }).catch(() => ({ data: [] }))
      .then(r => {
        const orders = r.data || [];
        const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);
        setStats({ revenue, orders: orders.length, aov: orders.length ? revenue / orders.length : 0 });
      });
    axios.get(`${API}/payment-settings`, { headers }).catch(() => ({ data: {} }))
      .then(r => {
        const d = r.data || {};
        setHasPayment(!!(d.stripe_connected || d.etransfer_verified || d.paypal_verified));
      });
  }, []);

  const steps = [
    { id: 'product', label: 'Add a product', done: products.length > 0, link: '/website-builder/products' },
    { id: 'payment', label: 'Connect a payment processor', done: hasPayment, link: '/website-builder/finances' },
    { id: 'fulfillment', label: 'Add a fulfillment method', done: false, link: '/website-builder/products' },
  ];
  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Products & Services"
        title="Products & Services"
        subtitle="Manage your storefront — products, orders, discounts, and selling tools."
        actions={
          <>
            <button className="p-2 text-[#9CA3AF] hover:text-white" data-testid="ps-settings"><Settings size={16} /></button>
            <SqsButton variant="primary" size="sm" data-testid="ps-create-btn">
              Create <ChevronDown size={12} />
            </SqsButton>
          </>
        }
      />

      <div className="p-10 max-w-7xl">
        {/* KPI CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          <Kpi label="Revenue" value={`$${stats.revenue.toFixed(2)}`} sub="Total income from your online store will appear here." testid="kpi-revenue" />
          <Kpi label="Orders" value={stats.orders} sub="Track your most recent sales here." testid="kpi-orders" />
          <Kpi label="Average Order Value" value={`$${stats.aov.toFixed(2)}`} sub="Track the average revenue per order here." testid="kpi-aov" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          {/* ONBOARDING CHECKLIST */}
          <div className="lg:col-span-2 bg-[#15152E] border border-white/[0.06] rounded-xl p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[14px] font-bold text-white">Start selling your physical products</h3>
              <ProgressRing pct={pct} />
            </div>
            <div className="mt-5 divide-y divide-white/[0.06]">
              {steps.map(s => (
                <Link key={s.id} to={s.link} className="flex items-center justify-between py-4 group cursor-pointer" data-testid={`ps-step-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${s.done ? 'border-[#A78BFA] bg-[#A78BFA]' : 'border-white/20'}`}>
                      {s.done && <Check size={11} className="text-[#0E0E23]" />}
                    </span>
                    <span className={`text-[13.5px] font-medium ${s.done ? 'text-[#64748B] line-through' : 'text-white'}`}>{s.label}</span>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-white/[0.04] group-hover:bg-[#A78BFA]/20 flex items-center justify-center transition-all">
                    <ArrowRight size={14} className="text-[#9CA3AF] group-hover:text-[#A78BFA]" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* TIP CARD */}
          <div className="bg-[#15152E] border border-white/[0.06] rounded-xl p-6 flex flex-col">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-2">Tip</p>
            <h4 className="text-[15px] font-bold text-white mb-2">Streamline your customer emails</h4>
            <p className="text-[12.5px] text-[#9CA3AF] leading-relaxed flex-1">
              Customize the emails customers automatically receive when they interact with your business.
            </p>
            <Link to="/website-builder/messages" className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold hover:text-white">
              <Mail size={12} /> Customize Emails
            </Link>
          </div>
        </div>

        {/* RECENTLY EDITED */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[13px] uppercase tracking-[0.18em] text-[#9CA3AF] font-bold">Recently Edited</h3>
          <Link to="/website-builder/products" className="text-[11.5px] uppercase tracking-[0.18em] text-white font-bold hover:text-[#A78BFA]">View All →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {recentEdited.map(p => (
            <Link key={p.id} to={`/website-builder/products`} className="bg-[#15152E] border border-white/[0.06] rounded-lg overflow-hidden hover:border-white/[0.18] transition-all" data-testid={`ps-recent-${p.id}`}>
              <div className="aspect-square bg-[#0E0E23]">
                {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
              </div>
              <div className="px-3 py-2">
                <p className="text-[12px] font-semibold text-white truncate">{p.name}</p>
                <p className="text-[11px] text-[#9CA3AF]">${(p.price || 0).toFixed(2)}</p>
              </div>
            </Link>
          ))}
          {recentEdited.length === 0 && (
            <div className="col-span-full text-center py-12 text-[#64748B] text-[12.5px]">
              No products yet. <Link to="/website-builder/products" className="text-[#A78BFA] underline">Add your first product →</Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-[#15152E] border border-white/[0.06] rounded-xl p-6" data-testid={testid}>
      <h3 className="text-[14px] font-bold text-white">{label}</h3>
      <p className="text-[11.5px] text-[#9CA3AF] mt-1 leading-relaxed">{sub}</p>
      <p className="text-[36px] font-bold text-white mt-6 font-syne tracking-tight">{value}</p>
    </div>
  );
}

function ProgressRing({ pct }) {
  const r = 14, c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <div className="relative">
      <svg width="36" height="36" className="-rotate-90">
        <circle cx="18" cy="18" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="3" fill="transparent" />
        <circle cx="18" cy="18" r={r} stroke="#A78BFA" strokeWidth="3" fill="transparent" strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">{pct}%</span>
    </div>
  );
}
